import {
  CapaQuotationUserRefundBankAccountModel,
  useFetchMyRefundBankAccountsQuery,
  useFetchQuotationUserRfqDetailQuery,
  useFetchWithdrawnQuotationPartnerQuery
} from '@generated/graphql';
import errorMessage from '@utils/errorMessage';
import { storage } from '@utils/storage';
import { useSnackbar } from 'notistack';
import { useRef } from 'react';
import useBaseData from '../useBaseData';

interface UseFetchPurchaseOrderBaseDataArgs {
  rfqId: string;
  partnerId: string;
}

const useFetchPurchaseOrderBaseData = ({
  rfqId,
  partnerId
}: UseFetchPurchaseOrderBaseDataArgs) => {
  const refundAccountRef = useRef<CapaQuotationUserRefundBankAccountModel | null>(
    null
  );
  const { enqueueSnackbar } = useSnackbar();

  const { loading: refundAccountLoading } = useFetchMyRefundBankAccountsQuery({
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const refundAccountList = result?.fetchMyRefundBankAccounts?.data;
      if (refundAccountList) {
        if (refundAccountList?.[0]) {
          const refundAccount = refundAccountList?.[0];

          refundAccountRef.current = refundAccount;
          storage.setItem.TEMP_REFUND_ACCOUNT_ID(refundAccount.id);
        }
      }
    }
  });

  const { data: baseData, loading: baseDataLoading } = useBaseData({
    rfqId,
    partnerId
  });

  const {
    data: fetchRfqDetail,
    loading: rfqLoading
  } = useFetchQuotationUserRfqDetailQuery({
    variables: { rfqId },
    skip: !rfqId,
    fetchPolicy: 'cache-and-network',
    onError: () => enqueueSnackbar(errorMessage.service, { variant: 'error' })
  });

  const {
    data: fetchPartner,
    loading: partnerLoading
  } = useFetchWithdrawnQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId
    },
    skip: !partnerId,
    fetchPolicy: 'cache-first',
    onError: () => enqueueSnackbar(errorMessage.service, { variant: 'error' })
  });

  const rfqDetail =
    fetchRfqDetail?.fetchQuotationUserRfqDetail.data ?? undefined;
  const partner =
    fetchPartner?.fetchWithdrawnQuotationPartner.data ?? undefined;

  const loading =
    refundAccountLoading || baseDataLoading || rfqLoading || partnerLoading;

  return {
    baseData,
    loading,
    rfqDetail,
    partner,
    refundAccount: refundAccountRef
  };
};

export default useFetchPurchaseOrderBaseData;
